/**
 * TenantThemeProvider — single source of truth for white-label theming.
 *
 * Per docs/00_PLATFORM_PLAN.md § UI Cohesion Layer 3:
 *   - System defaults → Tenant overrides → Customer (sub-tenant) overrides
 *     → per-level merge. (ThingsBoard's inheritance model.)
 *   - Theme override = CSS-variable swap on the `<html>` element. No React
 *     re-render storm.
 *
 * B0.38 expanded the contract from `{ logoUrl, productName, brandColors,
 * enabledPacks }` to a full TenantTheme — mode, 6-slot palette,
 * typography, density, radius, pattern, plus the legacy fields. The
 * provider injects a `<style id="tenant-theme-overrides">` block into
 * the document head + sets `data-theme` and `data-pattern` attributes
 * on the `<html>` element.
 *
 * Whose each part is (lib/theme/org-look, the 2026-10-06 next-phase plan, step 3): the org's look (palette, logo,
 * typography, density, radius, pattern) is saved with the org through GET/PUT /v1/platform/branding, applies only
 * inside it, and an org admin saves it; light, dark or the system's is each person's own, in this browser; the
 * product's name is nobody's (PRODUCT_NAME). An edit is a draft for the org it was made in until it is saved.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  effectiveTheme,
  LEGACY_THEME_KEY,
  lookOfTheme,
  PERSONAL_KEY,
  orgLookDirty,
  orgLookOf,
  personalOf,
  presetLook,
  type OrgLook,
  type PersonalTheme,
} from "@/lib/theme/org-look";
import { statusOf } from "@/lib/api/error";
import {
  DEFAULT_THEME,
  DENSITY_SCALE,
  RADIUS_SCALE,
  TYPOGRAPHY_STACKS,
  type BackgroundPattern,
  type DensityPreset,
  type PaletteOverride,
  type RadiusPreset,
  type TenantTheme,
  type ThemeMode,
  type TypographyPreset,
} from "@/lib/theme/types";
import { THEME_PRESETS, type ThemePreset } from "@/lib/theme/presets";

/**
 * Legacy shape kept for back-compat — every consumer that previously
 * called `useTenantTheme()` got `TenantBranding`. We project the new
 * `TenantTheme` down to it. New code should use `useTenantTheme()` and
 * read the full theme contract.
 */
export interface TenantBranding {
  logoUrl: string | null;
  productName: string;
  brandColors: {
    primary: string | null;
    primaryForeground: string | null;
    primaryMuted: string | null;
  };
  enabledPacks: string[];
}

export interface TenantThemeAdmin {
  // Existing — B0.25 pack toggle still works.
  togglePack: (pack: string) => void;
  setEnabledPacks: (packs: string[]) => void;

  // B0.38 — full theme control.
  setMode: (mode: ThemeMode) => void;
  setPalette: (palette: PaletteOverride) => void;
  setPaletteSlot: (slot: keyof PaletteOverride, rgb: string | null) => void;
  setTypography: (typography: TypographyPreset) => void;
  setDensity: (density: DensityPreset) => void;
  setRadius: (radius: RadiusPreset) => void;
  setPattern: (pattern: BackgroundPattern) => void;
  setLogoUrl: (url: string | null) => void;
  applyPreset: (presetId: string) => void;
  reset: () => void;
  /** Full snapshot — useful for export / debugging. */
  readonly theme: TenantTheme;
  readonly availablePresets: ThemePreset[];
  /** The org's look as edited here: unsaved edits, and saving them for the org (org admins). */
  readonly orgLook: OrgLookState;
}

/** The org's look as edited on this page (lib/theme/org-look): what saving it takes and does. */
export interface OrgLookState {
  /** There are edits to the look not saved for the org. */
  dirty: boolean;
  /** This person may save the org's look: an org admin of a live org. */
  canSave: boolean;
  saving: boolean;
  /** Why the last save failed, in words; null when it did not. */
  error: string | null;
  /** Saves the look shown for the active org, which then applies to everyone in it; true when it was saved. */
  save: () => Promise<boolean>;
  /** Drops the unsaved edits: the org's saved look shows again. */
  discard: () => void;
}

const TenantThemeContext = createContext<TenantTheme>(DEFAULT_THEME);
const TenantThemeAdminContext = createContext<TenantThemeAdmin | null>(null);

export function useTenantTheme(): TenantTheme {
  return useContext(TenantThemeContext);
}

/** Back-compat shim — projects the full theme down to the legacy shape. */
export function useTenantBranding(): TenantBranding {
  const theme = useTenantTheme();
  return useMemo<TenantBranding>(
    () => ({
      logoUrl: theme.logoUrl,
      productName: theme.productName,
      brandColors: {
        primary: theme.palette.brand ?? null,
        primaryForeground: null, // legacy field; not used in Phase-0
        primaryMuted: null,
      },
      enabledPacks: theme.enabledPacks,
    }),
    [theme],
  );
}

export function useTenantThemeAdmin(): TenantThemeAdmin {
  const ctx = useContext(TenantThemeAdminContext);
  if (!ctx) {
    throw new Error(
      "useTenantThemeAdmin must be used inside <TenantThemeProvider>",
    );
  }
  return ctx;
}

export interface TenantThemeProviderProps {
  /** The brand the address names before sign-in (a vendor's login page), laid under the org's look. */
  initial?: Partial<TenantTheme>;
  /** The active org (null before one is chosen): an edit of the look is a draft for it. */
  orgId?: string | null;
  /** The active org's saved look (lib/theme/org-look); none where it saved none. */
  saved?: Partial<OrgLook>;
  /** Saves a look for the active org; absent where this person may not (not an org admin, or no live org). */
  saveLook?: (look: OrgLook) => Promise<void>;
  children: ReactNode;
}

/** The person's own choices, from this browser: the new key, else what the old single key held (read once, then
 * removed, so an old browser-wide palette or name never comes back); the defaults when neither reads. */
function readPersonal(): PersonalTheme {
  if (typeof window === "undefined") return personalOf(null);
  try {
    const raw = window.localStorage.getItem(PERSONAL_KEY);
    if (raw) return personalOf(JSON.parse(raw));
    const legacy = window.localStorage.getItem(LEGACY_THEME_KEY);
    if (legacy) {
      window.localStorage.removeItem(LEGACY_THEME_KEY);
      return personalOf(JSON.parse(legacy));
    }
  } catch {
    /* unreadable or blocked storage: the defaults */
  }
  return personalOf(null);
}

function writePersonal(p: PersonalTheme) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PERSONAL_KEY, JSON.stringify(p));
  } catch {
    /* quota or disabled storage: nothing to do */
  }
}

type Draft = { orgId: string | null; look: Partial<OrgLook> } | null;

const NO_LOOK: Partial<OrgLook> = {};

export function TenantThemeProvider({
  initial,
  orgId = null,
  saved = NO_LOOK,
  saveLook,
  children,
}: TenantThemeProviderProps) {
  const activeOrgId = orgId;
  // The address's brand is read once, at mount, as it always was: a caller may build it anew on every render.
  const [brand] = useState(initial);
  const [personal, setPersonal] = useState<PersonalTheme>(readPersonal);
  const [draft, setDraft] = useState<Draft>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A look just saved: the draft stays until the org's saved look shows it, so the old look never flashes between.
  const [settling, setSettling] = useState<string | null>(null);

  const theme = useMemo(
    () => effectiveTheme({ brand, saved, draft, orgId: activeOrgId, personal }),
    [brand, saved, draft, activeOrgId, personal],
  );

  // Persist the person's own choices on every change.
  useEffect(() => {
    writePersonal(personal);
  }, [personal]);

  // Apply CSS-variable overrides + data-theme attribute on every change.
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // Listen for OS dark-mode flips when in `system` mode.
  useEffect(() => {
    if (theme.mode !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => applyThemeToDocument(theme);
    mql.addEventListener("change", listener);
    return () => mql.removeEventListener("change", listener);
  }, [theme]);

  // An edit of the look is a draft for the org it was made in: an edit after an org switch starts over.
  const edit = useCallback(
    (change: (look: Partial<OrgLook>) => Partial<OrgLook>) => {
      setError(null);
      setSettling(null);
      setDraft((d) => {
        const look = d && d.orgId === activeOrgId ? d.look : {};
        return { orgId: activeOrgId, look: change(look) };
      });
    },
    [activeOrgId],
  );

  const setMode = useCallback((mode: ThemeMode) => {
    setPersonal((p) => ({ ...p, mode }));
  }, []);

  const setPalette = useCallback(
    (palette: PaletteOverride) => edit((l) => ({ ...l, palette: { ...l.palette, ...palette } })),
    [edit],
  );

  const setPaletteSlot = useCallback(
    (slot: keyof PaletteOverride, rgb: string | null) => edit((l) => ({ ...l, palette: { ...l.palette, [slot]: rgb } })),
    [edit],
  );

  const setTypography = useCallback((typography: TypographyPreset) => edit((l) => ({ ...l, typography })), [edit]);
  const setDensity = useCallback((density: DensityPreset) => edit((l) => ({ ...l, density })), [edit]);
  const setRadius = useCallback((radius: RadiusPreset) => edit((l) => ({ ...l, radius })), [edit]);
  const setPattern = useCallback((pattern: BackgroundPattern) => edit((l) => ({ ...l, pattern })), [edit]);
  const setLogoUrl = useCallback((logoUrl: string | null) => edit((l) => ({ ...l, logoUrl })), [edit]);

  const applyPreset = useCallback(
    (presetId: string) => {
      const look = presetLook(theme, presetId);
      if (look) edit(() => look);
    },
    [theme, edit],
  );

  // The default look, as an edit to save: grey-blue and the platform's mark.
  const reset = useCallback(() => edit(() => lookOfTheme(DEFAULT_THEME)), [edit]);

  const togglePack = useCallback((pack: string) => {
    setPersonal((p) => ({
      ...p,
      enabledPacks: p.enabledPacks.includes(pack) ? p.enabledPacks.filter((x) => x !== pack) : [...p.enabledPacks, pack],
    }));
  }, []);

  const setEnabledPacks = useCallback((packs: string[]) => {
    setPersonal((p) => ({ ...p, enabledPacks: packs }));
  }, []);

  const dirty = orgLookDirty(draft, activeOrgId, settling !== null);
  const canSave = saveLook !== undefined;
  const save = useCallback(async (): Promise<boolean> => {
    if (!saveLook) {
      setError("Only an org admin saves the org's look.");
      return false;
    }
    setSaving(true);
    setError(null);
    const look = lookOfTheme(theme);
    try {
      await saveLook(look);
      setSettling(JSON.stringify(orgLookOf(look)));
      return true;
    } catch (err) {
      setError(
        statusOf(err) === 403 ? "Only an org admin saves the org's look." : "The look was not saved. Try again.",
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [saveLook, theme]);
  // The draft goes once the saved look is the one just saved, or after a moment if it never reads back the same.
  useEffect(() => {
    if (settling === null) return;
    const done = () => {
      setDraft(null);
      setSettling(null);
    };
    if (JSON.stringify(orgLookOf(saved)) === settling) {
      done();
      return;
    }
    const t = setTimeout(done, 2_000);
    return () => clearTimeout(t);
  }, [settling, saved]);
  const discard = useCallback(() => {
    setDraft(null);
    setSettling(null);
    setError(null);
  }, []);

  const admin = useMemo<TenantThemeAdmin>(
    () => ({
      togglePack,
      setEnabledPacks,
      setMode,
      setPalette,
      setPaletteSlot,
      setTypography,
      setDensity,
      setRadius,
      setPattern,
      setLogoUrl,
      applyPreset,
      reset,
      theme,
      availablePresets: THEME_PRESETS,
      orgLook: { dirty, canSave, saving, error, save, discard },
    }),
    [
      togglePack,
      setEnabledPacks,
      setMode,
      setPalette,
      setPaletteSlot,
      setTypography,
      setDensity,
      setRadius,
      setPattern,
      setLogoUrl,
      applyPreset,
      reset,
      theme,
      dirty,
      canSave,
      saving,
      error,
      save,
      discard,
    ],
  );

  return (
    <TenantThemeContext.Provider value={theme}>
      <TenantThemeAdminContext.Provider value={admin}>{children}</TenantThemeAdminContext.Provider>
    </TenantThemeContext.Provider>
  );
}

/**
 * Apply the theme to the document: mode attribute, palette CSS vars,
 * font stack, radius / density scales, background pattern. Idempotent.
 */
function applyThemeToDocument(theme: TenantTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  // Mode — data-theme drives the dark-mode CSS selectors in index.css.
  root.setAttribute("data-theme", theme.mode);

  // Pattern — single attribute drives the background-image preset.
  root.setAttribute("data-pattern", theme.pattern);

  // Palette + auto-derived foreground / muted tints — injected into a
  // dedicated <style> element so we can swap multiple variables atomically.
  let style = document.getElementById("tenant-theme-overrides") as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = "tenant-theme-overrides";
    document.head.appendChild(style);
  }
  style.textContent = buildOverridesCss(theme);
}

/**
 * Build the CSS body for the `<style id="tenant-theme-overrides">` tag.
 *
 * Specificity matters: the dark-mode rules in `index.css` target
 * `:root[data-theme="dark"]` (specificity 0,2,0). To make user-palette
 * overrides win regardless of mode, we target `:root[data-theme]`
 * (also 0,2,0) — same specificity, but our style tag is appended after
 * the link, so order resolves in our favour.
 *
 * Net effect: user picks Foundry Amber (brand = orange) and toggles to
 * dark mode → dark-mode CSS swaps surface + text to slate, our override
 * keeps brand orange (and auto-derives the muted/foreground for that
 * orange against the dark surface).
 */
function buildOverridesCss(theme: TenantTheme): string {
  // Resolve "system" mode → actual dark/light by reading the OS pref.
  const effectiveDark = isEffectivelyDark(theme.mode);

  const colorVars: Record<string, string> = {
    "--font-sans": TYPOGRAPHY_STACKS[theme.typography],
    "--density-scale": String(DENSITY_SCALE[theme.density]),
    "--radius-scale": String(RADIUS_SCALE[theme.radius]),
  };

  for (const slot of [
    "brand",
    "accent",
    "success",
    "warning",
    "danger",
    "info",
  ] as const) {
    const v = theme.palette[slot];
    if (v) {
      colorVars[`--color-${slot}`] = v;
      const derived = deriveMutedAndForeground(v, effectiveDark);
      colorVars[`--color-${slot}-muted`] = derived.muted;
      colorVars[`--color-${slot}-foreground`] = derived.foreground;
    }
  }

  const block = Object.entries(colorVars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n");

  // `:root[data-theme]` matches any data-theme value (light/dark/system),
  // with specificity equal to `:root[data-theme="dark"]` — so this rule
  // wins on order, not specificity.
  return `:root[data-theme] {\n${block}\n}`;
}

/**
 * True when the user is effectively in dark mode (manual "dark" or
 * "system" + OS prefers dark). Used by the colour-deriver to pick the
 * right luma direction.
 */
function isEffectivelyDark(mode: ThemeMode): boolean {
  if (mode === "dark") return true;
  if (mode === "light") return false;
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Given a base RGB triple ("R G B"), derive sensible `muted` and
 * `foreground` tints. Used when the user picks a custom palette colour
 * — keeps the design system internally consistent without forcing them
 * to choose all three.
 *
 * `isDark` is the *effective* mode (system → resolved OS pref), so the
 * derivation is correct under "system" too.
 */
function deriveMutedAndForeground(
  rgb: string,
  isDark: boolean,
): { muted: string; foreground: string } {
  const [r, g, b] = rgb.split(/\s+/).map((s) => Number(s));
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return { muted: rgb, foreground: "255 255 255" };
  }
  // Relative luminance — WCAG style.
  const luma = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  // Foreground: pick black or white for max contrast.
  const fg = luma > 0.55 ? "15 23 42" : "255 255 255";
  // Muted: in light mode, lighten toward white; in dark, darken toward base.
  const t = isDark ? 0.25 : 0.85;
  const mix = (c: number) => Math.round(c + (isDark ? -c : 255 - c) * t);
  const muted = `${mix(r)} ${mix(g)} ${mix(b)}`;
  return { muted, foreground: fg };
}

/**
 * Helper: is a given pack enabled for the current tenant?
 *
 * Used to gate Tier 2 routes (`/app/twin/*` only loads when
 * `isPackEnabled('foundry')`).
 *
 * Accepts either the new `TenantTheme` or the legacy `TenantBranding`
 * since both carry `enabledPacks`.
 */
export function isPackEnabled(
  theme: { enabledPacks: string[] },
  pack: string,
): boolean {
  return theme.enabledPacks.includes(pack);
}
